import { Form, InputNumber, Modal, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { ReactElement, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import { applicationPropertyValuerQuoteReceivedAction } from '~Applications/actions';
import DatePicker from '~UI/DatePicker';
import TimePicker from '~UI/TimePicker';
import IValuer from '~Valuers/IValuer';

export default function EnterQuoteModal(props: {
    applicationProperty: IApplicationProperty;
    isOpen: boolean;
    onCancel: () => void;
    valuer: IValuer;
}): ReactElement {
    const {
        applicationProperty,
        isOpen,
        onCancel,
        valuer,
    } = props;

    const dispatch: Dispatch = useDispatch();

    const [ quoteAmount, setQuoteAmount ] = useState<number | null>(null);
    const [ quoteReceivedDateTime, setQuoteReceivedDateTime ] = useState<string | null>(null);
    const [ quoteTimeFrameDays, setQuoteTimeFrameDays ] = useState<number | null>(null);

    const quoteReceivedDateTimeDayjs: Dayjs | null = quoteReceivedDateTime ? dayjs(quoteReceivedDateTime) : null;

    const onChangeDate: (date: Dayjs) => void = useCallback((date: Dayjs): void => {
        setQuoteReceivedDateTime((previousQuoteReceivedDateTime: string | null): string => dayjs(
            `${date.format('YYYY-MM-DD')} ${dayjs(previousQuoteReceivedDateTime || undefined).format('HH:mm')}`,
        ).format());
    }, []);

    const onChangeTime: (time: Dayjs) => void = useCallback((time: Dayjs): void => {
        setQuoteReceivedDateTime((previousQuoteReceivedDateTime: string | null): string => dayjs(
            `${dayjs(previousQuoteReceivedDateTime || undefined).format('YYYY-MM-DD')} ${time.format('HH:mm')}`,
        ).format());
    }, []);

    const onChangeQuoteAmount: (value: number) => void = useCallback((value: number): void => {
        setQuoteAmount(value);
    }, []);

    const onChangeQuoteTimeFrameDays: (value: number) => void = useCallback((value: number): void => {
        setQuoteTimeFrameDays(value);
    }, []);

    const onClickOk: () => void = useCallback((): void => {
        dispatch(applicationPropertyValuerQuoteReceivedAction(
            applicationProperty.uuid,
            valuer.uuid,
            quoteAmount,
            quoteReceivedDateTime,
            quoteTimeFrameDays,
        ));

        onCancel();
    }, [
        applicationProperty.uuid,
        dispatch,
        onCancel,
        quoteAmount,
        quoteReceivedDateTime,
        quoteTimeFrameDays,
        valuer.uuid,
    ]);

    return (
        <Modal
            okText='Enter Quote'
            onCancel={onCancel}
            onOk={onClickOk}
            open={isOpen}
            title='Enter Valuation Quote Information'
            wrapClassName='property-valuation-enter-quote-modal'
        >
            <Form.Item className='quote-received-date' label='Received Date'>
                <Space>
                    <DatePicker
                        className='quote-received-date-datepicker'
                        format='DD/MM/YYYY'
                        onChange={onChangeDate}
                        value={quoteReceivedDateTimeDayjs || dayjs()}
                    />
                    <TimePicker
                        className='quote-received-date-timepicker'
                        format='H:mm'
                        onChange={onChangeTime}
                        value={quoteReceivedDateTimeDayjs || dayjs()}
                    />
                </Space>
            </Form.Item>
            <Form.Item className='amount' label='Amount'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeQuoteAmount}
                    value={quoteAmount}
                />
            </Form.Item>
            <Form.Item className='time-frame' label='Time Frame'>
                <InputNumber
                    addonAfter='days'
                    min={0}
                    onChange={onChangeQuoteTimeFrameDays}
                    value={quoteTimeFrameDays}
                />
            </Form.Item>
        </Modal>
    );
}
